import { cn } from '@listening/utils/react-helpers';
import * as ScrollAreaPrimitive from '@radix-ui/react-scroll-area';
import type * as React from 'react';

const ScrollArea = ({
  ref,
  className,
  children,
  viewPortClassName,
  blurY,
  useBlockFix,
  useFlexFix,
  viewportRef,
  ...props
}: React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.Root> & {
  viewPortClassName?: string;
  blurY?: boolean | 'top' | 'bottom';
  useBlockFix?: boolean; // https://github.com/radix-ui/primitives/issues/926#issuecomment-1447283516
  useFlexFix?: boolean;
  viewportRef?: React.Ref<HTMLDivElement>;
} & {
  ref?: React.RefObject<React.ComponentRef<
    typeof ScrollAreaPrimitive.Root
  > | null>;
}) => (
  <ScrollAreaPrimitive.Root
    className={cn('relative flex flex-col overflow-hidden', className)}
    ref={ref}
    {...props}
  >
    <ScrollAreaPrimitive.Viewport
      className={cn(
        'h-full w-full rounded-[inherit]',
        viewPortClassName ?? '',
        useBlockFix && '[&>div]:block!',
        useFlexFix && '[&>div]:flex! [&>div]:flex-col',
      )}
      ref={viewportRef}
    >
      {(blurY === true || blurY === 'top') && (
        <div className='absolute inset-x-0 -top-px z-10 h-2 bg-linear-to-t from-transparent to-background' />
      )}
      {children}
      {(blurY === true || blurY === 'bottom') && (
        <div className='absolute inset-x-0 -bottom-px z-10 h-2 bg-linear-to-b from-transparent to-background' />
      )}
    </ScrollAreaPrimitive.Viewport>
    <ScrollBar />
    <ScrollBar orientation='horizontal' />
    <ScrollAreaPrimitive.Corner />
  </ScrollAreaPrimitive.Root>
);
ScrollArea.displayName = ScrollAreaPrimitive.Root.displayName;

const ScrollBar = ({
  ref,
  className,
  orientation = 'vertical',
  ...props
}: React.ComponentPropsWithoutRef<
  typeof ScrollAreaPrimitive.ScrollAreaScrollbar
> & {
  ref?: React.RefObject<React.ComponentRef<
    typeof ScrollAreaPrimitive.ScrollAreaScrollbar
  > | null>;
}) => (
  <ScrollAreaPrimitive.ScrollAreaScrollbar
    className={cn(
      'flex touch-none transition-colors select-none',
      orientation === 'vertical' &&
        'h-full w-2.5 border-l border-l-transparent p-[1px]',
      orientation === 'horizontal' &&
        'h-2.5 flex-col border-t border-t-transparent p-[1px]',
      className,
    )}
    orientation={orientation}
    ref={ref}
    {...props}
  >
    <ScrollAreaPrimitive.ScrollAreaThumb
      className={cn('relative flex-1 rounded-full bg-border')}
    />
  </ScrollAreaPrimitive.ScrollAreaScrollbar>
);
ScrollBar.displayName = ScrollAreaPrimitive.ScrollAreaScrollbar.displayName;

export { ScrollArea, ScrollBar };
